import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';

function clp(n) {
  return `$${Number(n || 0).toLocaleString('es-CL')}`;
}

function fmtFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CL');
}

export default function IngresoCobroPanel({ cid, row, periodoServicioLabel, onClose, onPaid }) {
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fechaPago, setFechaPago] = useState(() => new Date().toISOString().slice(0, 10));
  const [medio, setMedio] = useState('transferencia');
  const [importe, setImporte] = useState('');
  const [sel, setSel] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!cid || !row?.id) return;
    setLoading(true);
    setError('');
    api
      .get(`/gastos-comunes/condominios/${cid}/unidades/${row.id}/cargos-pendientes`)
      .then((r) => {
        setCargos(r.data);
        const init = {};
        r.data.forEach((c) => {
          init[c.cargoId] = { checked: false, importe: String(c.saldo) };
        });
        setSel(init);
      })
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [cid, row?.id]);

  const aplicacionesBody = useMemo(() => {
    const lines = [];
    for (const c of cargos) {
      const s = sel[c.cargoId];
      if (!s?.checked) continue;
      const imp = Math.round(Number(s.importe));
      if (imp > 0) lines.push({ cargoId: c.cargoId, importe: imp });
    }
    return lines;
  }, [cargos, sel]);

  const runPreview = useCallback(async () => {
    const monto = Math.round(Number(importe));
    if (!monto || monto < 1 || !aplicacionesBody.length) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    try {
      const r = await api.post(`/gastos-comunes/condominios/${cid}/pagos/preview`, {
        unidadId: row.id,
        importe: monto,
        aplicaciones: aplicacionesBody
      });
      setPreview(r.data);
      setError('');
    } catch (e) {
      setPreview(null);
      setError(e.response?.data?.error || e.message);
    } finally {
      setPreviewLoading(false);
    }
  }, [aplicacionesBody, cid, importe, row?.id]);

  useEffect(() => {
    const t = setTimeout(runPreview, 300);
    return () => clearTimeout(t);
  }, [runPreview]);

  const toggleCargo = (cargoId, checked) => {
    setSel((prev) => ({
      ...prev,
      [cargoId]: { ...prev[cargoId], checked }
    }));
  };

  const pagarMes = (cargoId) => {
    const c = cargos.find((x) => x.cargoId === cargoId);
    if (!c) return;
    setSel((prev) => {
      const next = { ...prev };
      cargos.forEach((x) => {
        next[x.cargoId] = { checked: false, importe: String(x.saldo) };
      });
      next[cargoId] = { checked: true, importe: String(c.saldo) };
      return next;
    });
    setImporte(String(c.saldo));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!aplicacionesBody.length) {
      setError('Marque el mes/período al que aplica el pago.');
      return;
    }
    setError('');
    try {
      await api.post(`/gastos-comunes/condominios/${cid}/pagos`, {
        unidadId: row.id,
        importe: Number(importe),
        fechaPago: new Date(fechaPago).toISOString(),
        medio,
        aplicaciones: aplicacionesBody
      });
      onPaid?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  if (!row) return null;

  return (
    <div className="ingreso-overlay ingreso-overlay-modal" role="presentation" onClick={onClose}>
      <div className="ingreso-pago-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
        <header className="ingreso-cobro-head">
          <div>
            <p className="ingreso-cobro-kicker">Registrar pago</p>
            <h2 className="ingreso-cobro-title">
              {row.codigoPropiedad} · {row.titularNombre || '—'}
            </h2>
            <p className="ingreso-cobro-sub">
              Cobro {periodoServicioLabel || '—'} · Deuda {clp(row.totalPagar)} · Nº operación automático (folio)
            </p>
          </div>
          <button type="button" className="btn-ghost ingreso-cobro-close" onClick={onClose}>
            ×
          </button>
        </header>

        {error && <div className="alert alert-error">{error}</div>}

        {loading ? (
          <p className="empty-hint">Cargando…</p>
        ) : !cargos.length ? (
          <p className="empty-hint">Sin cargos pendientes.</p>
        ) : (
          <>
            <p className="pago-hint">Marque el mes que paga e indique monto parcial si corresponde.</p>
            <div className="ingreso-cargos-table-wrap">
              <table className="ingreso-cargos-table">
                <thead>
                  <tr>
                    <th />
                    <th>Período</th>
                    <th>Saldo</th>
                    <th>Imputar</th>
                  </tr>
                </thead>
                <tbody>
                  {cargos.map((c) => {
                    const s = sel[c.cargoId] || { checked: false, importe: String(c.saldo) };
                    return (
                      <tr key={c.cargoId}>
                        <td>
                          <input
                            type="checkbox"
                            checked={s.checked}
                            onChange={(ev) => toggleCargo(c.cargoId, ev.target.checked)}
                          />
                        </td>
                        <td>
                          <button type="button" className="btn-link btn-link-block" onClick={() => pagarMes(c.cargoId)}>
                            {c.periodoLabel}
                          </button>
                          <div className="ingreso-cargo-meta">Vence {fmtFecha(c.vencimiento)}</div>
                        </td>
                        <td>{clp(c.saldo)}</td>
                        <td>
                          <input
                            type="number"
                            min="1"
                            max={c.saldo}
                            className="ingreso-imputar-input"
                            disabled={!s.checked}
                            value={s.importe}
                            onChange={(ev) =>
                              setSel((prev) => ({
                                ...prev,
                                [c.cargoId]: { checked: true, importe: ev.target.value }
                              }))
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <form className="ingreso-pago-form" onSubmit={submit}>
              <div className="ingreso-pago-grid">
                <div className="field">
                  <label>Monto recibido CLP</label>
                  <input type="number" min="1" value={importe} onChange={(e) => setImporte(e.target.value)} required />
                </div>
                <div className="field">
                  <label>Fecha de pago</label>
                  <input type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} required />
                </div>
                <div className="field">
                  <label>Medio</label>
                  <select value={medio} onChange={(e) => setMedio(e.target.value)}>
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              </div>

              <div className="ingreso-preview card card-flat">
                <h3 className="card-title-sm">Imputación</h3>
                {previewLoading && <p className="empty-hint">Calculando…</p>}
                {!previewLoading && preview?.aplicaciones?.length ? (
                  <ul className="ingreso-preview-list">
                    {preview.aplicaciones.map((a) => (
                      <li key={a.cargoId}>
                        <span>{a.periodoLabel}</span>
                        <strong>{clp(a.importe)}</strong>
                      </li>
                    ))}
                  </ul>
                ) : (
                  !previewLoading && <p className="empty-hint">Seleccione período y monto.</p>
                )}
              </div>

              <div className="ingreso-pago-actions">
                <button
                  type="submit"
                  className="btn"
                  disabled={!importe || Number(importe) < 1 || !aplicacionesBody.length}
                >
                  Confirmar pago
                </button>
                <button type="button" className="btn secondary" onClick={onClose}>
                  Cancelar
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
